import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { usePdf } from '../store/pdfStore.js';
import { useConfig } from '../store/configStore.js';
import { fontFamilyToCss } from '../utils/pdfOps.js';
import { useT } from '../i18n/index.js';
import { Icon } from './Icon.jsx';

const MIN_RECT = 4;
const MIN_LINE = 4;

function AnnotationLayerBase({ page, size, scale, defaults }) {
  const t = useT();
  const tool = usePdf((s) => s.tool);
  const setTool = usePdf((s) => s.setTool);
  const pendingSig = usePdf((s) => s.pendingSignature);
  const setPendingSig = usePdf((s) => s.setPendingSignature);
  const anns = usePdf((s) => s.annotations[page.id]) || [];
  const addAnnotation = usePdf((s) => s.addAnnotation);
  const updateAnnotation = usePdf((s) => s.updateAnnotation);
  const updateLive = usePdf((s) => s.updateAnnotationLive);
  const beginEdit = usePdf((s) => s.beginEdit);
  const deleteAnn = usePdf((s) => s.deleteAnnotation);
  const reorderAnn = usePdf((s) => s.reorderAnnotation);
  const toggleTextSelection = usePdf((s) => s.toggleTextSelection);

  const editorCfg = useConfig((s) => s.config.settings.editor);
  const drawShape = editorCfg.drawShape || 'free';
  const drawColor = editorCfg.shapeColor || '#ef4444';
  const drawStroke = editorCfg.strokeWidth || 2;

  const [selectedId, setSelectedId] = useState(null);
  const [drawing, setDrawing] = useState(null);
  const [penPoints, setPenPoints] = useState(null);
  const layerRef = useRef(null);

  const toPdf = useCallback(
    (clientX, clientY) => {
      const rect = layerRef.current.getBoundingClientRect();
      return { x: (clientX - rect.left) / scale, y: (clientY - rect.top) / scale };
    },
    [scale]
  );

  const onLayerMouseDown = (e) => {
    if (tool === 'select') return;
    if (e.target !== layerRef.current) return;
    e.preventDefault();
    setSelectedId(null);
    const { x, y } = toPdf(e.clientX, e.clientY);

    if (tool === 'text') {
      const ann = {
        id: crypto.randomUUID(),
        type: 'text',
        x,
        y: y - defaults.size / 2,
        text: '',
        font: defaults.font,
        size: defaults.size,
        color: defaults.color,
        bold: defaults.bold,
        italic: defaults.italic,
        underline: !!editorCfg.defaultUnderline,
        strike: !!editorCfg.defaultStrike,
      };
      addAnnotation(page.id, ann);
      setSelectedId(ann.id);
      setTool('select');
    } else if (tool === 'sign' && pendingSig) {
      const ratio = pendingSig.w && pendingSig.h ? pendingSig.h / pendingSig.w : 0.4;
      const w = 160;
      const h = w * ratio;
      const ann = {
        id: crypto.randomUUID(),
        type: 'image',
        dataUrl: pendingSig.dataUrl,
        x: x - w / 2,
        y: y - h / 2,
        w,
        h,
        isSignature: true,
      };
      addAnnotation(page.id, ann);
      setSelectedId(ann.id);
      setPendingSig(null);
      setTool('select');
    } else if (tool === 'draw') {
      if (drawShape === 'free') {
        setPenPoints([{ x, y }]);
      } else {
        setDrawing({ tool: 'draw', shape: drawShape, startX: x, startY: y, x, y, w: 0, h: 0 });
      }
    }
  };

  const onLayerMouseMove = (e) => {
    if (drawing) {
      const { x, y } = toPdf(e.clientX, e.clientY);
      if (drawing.shape === 'line') {
        setDrawing({ ...drawing, endX: x, endY: y, w: Math.abs(x - drawing.startX), h: Math.abs(y - drawing.startY) });
      } else if (drawing.shape === 'circle') {
        const dx = x - drawing.startX;
        const dy = y - drawing.startY;
        const r = Math.hypot(dx, dy);
        setDrawing({ ...drawing, x: drawing.startX - r, y: drawing.startY - r, w: r * 2, h: r * 2, cx: drawing.startX, cy: drawing.startY, r });
      } else {
        const nx = Math.min(drawing.startX, x);
        const ny = Math.min(drawing.startY, y);
        const w = Math.abs(x - drawing.startX);
        const h = Math.abs(y - drawing.startY);
        setDrawing({ ...drawing, x: nx, y: ny, w, h });
      }
    } else if (penPoints) {
      const { x, y } = toPdf(e.clientX, e.clientY);
      const last = penPoints[penPoints.length - 1];
      if (Math.hypot(x - last.x, y - last.y) > 1.2) {
        setPenPoints([...penPoints, { x, y }]);
      }
    }
  };

  const onLayerMouseUp = () => {
    if (drawing) {
      const big = drawing.shape === 'line'
        ? Math.hypot((drawing.endX ?? drawing.startX) - drawing.startX, (drawing.endY ?? drawing.startY) - drawing.startY) > MIN_LINE
        : drawing.w > MIN_RECT && drawing.h > MIN_RECT;
      if (big) {
        let ann;
        if (drawing.shape === 'line') {
          ann = {
            id: crypto.randomUUID(),
            type: 'shape',
            shape: 'line',
            x1: drawing.startX,
            y1: drawing.startY,
            x2: drawing.endX,
            y2: drawing.endY,
            color: drawColor,
            strokeWidth: drawStroke,
          };
        } else if (drawing.shape === 'circle') {
          ann = {
            id: crypto.randomUUID(),
            type: 'shape',
            shape: 'circle',
            cx: drawing.cx,
            cy: drawing.cy,
            r: drawing.r,
            color: drawColor,
            strokeWidth: drawStroke,
          };
        } else {
          ann = {
            id: crypto.randomUUID(),
            type: 'shape',
            shape: drawing.shape || 'rect',
            x: drawing.x,
            y: drawing.y,
            w: drawing.w,
            h: drawing.h,
            color: drawColor,
            strokeWidth: drawStroke,
          };
        }
        addAnnotation(page.id, ann);
        setSelectedId(ann.id);
        setTool('select');
      }
      setDrawing(null);
    } else if (penPoints) {
      if (penPoints.length > 1) {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const p of penPoints) {
          if (p.x < minX) minX = p.x;
          if (p.y < minY) minY = p.y;
          if (p.x > maxX) maxX = p.x;
          if (p.y > maxY) maxY = p.y;
        }
        const ann = {
          id: crypto.randomUUID(),
          type: 'draw',
          points: penPoints,
          bbox: { x: minX, y: minY, w: maxX - minX, h: maxY - minY },
          color: drawColor,
          strokeWidth: drawStroke,
        };
        addAnnotation(page.id, ann);
        setSelectedId(ann.id);
        setTool('select');
      }
      setPenPoints(null);
    }
  };

  const layerCursor =
    tool === 'text' ? 'text' :
    tool === 'draw' ? 'crosshair' :
    tool === 'sign' && pendingSig ? 'copy' :
    'default';

  const showHint = tool !== 'select';
  const hintText =
    tool === 'text' ? t('editor.click_to_add_text') :
    tool === 'sign' && pendingSig ? t('editor.click_to_add_sig') :
    tool === 'draw' ? (drawShape === 'free' ? t('editor.drag_to_draw') : t(`editor.drag_to_draw_${drawShape}`)) :
    '';

  return (
    <div
      ref={layerRef}
      className="ann-layer"
      style={{
        width: size.width,
        height: size.height,
        cursor: layerCursor,
        pointerEvents: tool === 'select' ? 'none' : 'auto',
      }}
      onMouseDown={onLayerMouseDown}
      onMouseMove={onLayerMouseMove}
      onMouseUp={onLayerMouseUp}
      onMouseLeave={onLayerMouseUp}
    >
      {showHint && hintText && <div className="ann-hint">{hintText}</div>}

      {anns.map((a) => (
        <Annotation
          key={a.id}
          ann={a}
          scale={scale}
          selected={selectedId === a.id}
          onSelect={() => {
            setSelectedId(a.id);
            // A bound text override mirrors an existing glyph: selecting
            // it also puts it in the text-selection so the restyle bar
            // shows with its current style.
            if (a.type === 'text' && a.originalItemKey) {
              toggleTextSelection(
                {
                  pageId: page.id,
                  itemKey: a.originalItemKey,
                  x: a.x,
                  y: a.y,
                  w: a.w,
                  h: a.h,
                  fontSize: a.size,
                  font: a.font,
                  color: a.color,
                  bg: a.background,
                  bold: a.bold,
                  italic: a.italic,
                  str: a.text,
                },
                false
              );
            }
          }}
          onBeginEdit={beginEdit}
          onLiveUpdate={(patch) => updateLive(page.id, a.id, patch)}
          onCommit={(patch) => {
            if (patch) updateAnnotation(page.id, a.id, patch);
          }}
          onDelete={() => {
            deleteAnn(page.id, a.id);
            setSelectedId(null);
          }}
          onReorder={(dir) => reorderAnn(page.id, a.id, dir)}
        />
      ))}

      {drawing && drawing.shape !== 'line' && drawing.shape !== 'ellipse' && drawing.shape !== 'circle' && (
        <div
          className={`ann-draft ${drawing.tool} ${drawing.shape || ''}`}
          style={{
            left: drawing.x * scale,
            top: drawing.y * scale,
            width: drawing.w * scale,
            height: drawing.h * scale,
            background: drawing.shape === 'fillrect' ? drawColor : undefined,
            borderColor:
              drawing.tool === 'draw' && drawing.shape !== 'fillrect' ? drawColor : undefined,
            borderWidth:
              drawing.tool === 'draw' && drawing.shape !== 'fillrect'
                ? drawStroke * scale
                : drawing.shape === 'fillrect'
                ? 0
                : undefined,
          }}
        />
      )}
      {drawing && (drawing.shape === 'ellipse' || drawing.shape === 'circle' || drawing.shape === 'line') && (
        <svg className="ann-draft-svg" style={{ width: size.width, height: size.height }}>
          {drawing.shape === 'line' && (
            <line
              x1={drawing.startX * scale}
              y1={drawing.startY * scale}
              x2={(drawing.endX ?? drawing.startX) * scale}
              y2={(drawing.endY ?? drawing.startY) * scale}
              stroke={drawColor}
              strokeWidth={drawStroke * scale}
              strokeLinecap="round"
            />
          )}
          {drawing.shape === 'ellipse' && (
            <ellipse
              cx={(drawing.x + drawing.w / 2) * scale}
              cy={(drawing.y + drawing.h / 2) * scale}
              rx={(drawing.w / 2) * scale}
              ry={(drawing.h / 2) * scale}
              fill="none"
              stroke={drawColor}
              strokeWidth={drawStroke * scale}
            />
          )}
          {drawing.shape === 'circle' && (
            <circle
              cx={drawing.cx * scale}
              cy={drawing.cy * scale}
              r={drawing.r * scale}
              fill="none"
              stroke={drawColor}
              strokeWidth={drawStroke * scale}
            />
          )}
        </svg>
      )}

      {penPoints && penPoints.length > 1 && (
        <svg className="ann-draft-svg" style={{ width: size.width, height: size.height }}>
          <polyline
            points={penPoints.map((p) => `${p.x * scale},${p.y * scale}`).join(' ')}
            fill="none"
            stroke={drawColor}
            strokeWidth={drawStroke * scale}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}

function Annotation({ ann, scale, selected, onSelect, onBeginEdit, onLiveUpdate, onCommit, onDelete, onReorder }) {
  const layerProps = {
    onFront: () => onReorder?.('front'),
    onBack: () => onReorder?.('back'),
  };
  const dragRef = useRef(null);
  const startRef = useRef(null);
  const mode = useRef(null);

  const onMouseDown = (e, _mode = 'move') => {
    e.stopPropagation();
    onSelect();
    mode.current = _mode;
    const points = ann.points ? ann.points.map((p) => ({ ...p })) : null;
    let rotCenter = null;
    let startPointerAngle = 0;
    if (_mode === 'rotate' && dragRef.current) {
      const r = dragRef.current.getBoundingClientRect();
      rotCenter = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      startPointerAngle = Math.atan2(e.clientY - rotCenter.y, e.clientX - rotCenter.x);
    }
    startRef.current = {
      mx: e.clientX,
      my: e.clientY,
      x: ann.x,
      y: ann.y,
      w: ann.w,
      h: ann.h,
      points,
      bbox: ann.bbox ? { ...ann.bbox } : null,
      rotCenter,
      startPointerAngle,
      startRotation: ann.rotation || 0,
      moved: false,
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onMove = (e) => {
    const s = startRef.current;
    if (!s) return;
    const dx = (e.clientX - s.mx) / scale;
    const dy = (e.clientY - s.my) / scale;
    if (mode.current !== 'rotate' && Math.abs(dx) < 1 && Math.abs(dy) < 1 && !s.moved) return;
    if (!s.moved) {
      s.moved = true;
      onBeginEdit?.();
    }
    if (mode.current === 'move') {
      if (s.points) {
        const newPoints = s.points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
        const newBbox = s.bbox ? { x: s.bbox.x + dx, y: s.bbox.y + dy, w: s.bbox.w, h: s.bbox.h } : null;
        onLiveUpdate({ points: newPoints, bbox: newBbox });
      } else {
        onLiveUpdate({ x: s.x + dx, y: s.y + dy });
      }
    } else if (mode.current === 'resize') {
      const w = Math.max(20, s.w + dx);
      const h = ann.type === 'image' && s.w ? w * (s.h / s.w) : Math.max(12, s.h + dy);
      onLiveUpdate({ w, h });
    } else if (mode.current === 'rotate' && s.rotCenter) {
      const ang = Math.atan2(e.clientY - s.rotCenter.y, e.clientX - s.rotCenter.x);
      let deg = s.startRotation + ((ang - s.startPointerAngle) * 180) / Math.PI;
      if (e.shiftKey) deg = Math.round(deg / 15) * 15; // snap with Shift
      onLiveUpdate({ rotation: Math.round(deg) });
    }
  };

  const onUp = () => {
    startRef.current = null;
    mode.current = null;
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
  };

  if (ann.type === 'rect' || ann.type === 'highlight') {
    return (
      <div
        ref={dragRef}
        className={`ann ann-rect ${ann.type === 'highlight' ? 'ann-highlight' : ''} ${selected ? 'selected' : ''}`}
        style={{
          left: ann.x * scale,
          top: ann.y * scale,
          width: ann.w * scale,
          height: ann.h * scale,
          background: ann.color || (ann.type === 'highlight' ? '#fde047' : '#fff'),
          opacity: ann.opacity ?? 1,
        }}
        onMouseDown={(e) => onMouseDown(e, 'move')}
      >
        {selected && <Handles onResize={(e) => onMouseDown(e, 'resize')} onDelete={onDelete} {...layerProps} />}
      </div>
    );
  }

  if (ann.type === 'shape') {
    const sw = (ann.strokeWidth || 2) * scale;
    const col = ann.color || '#ef4444';
    const kind = ann.shape || 'rect';
    if (kind === 'circle') {
      const cx = ann.cx, cy = ann.cy, r = ann.r;
      const pad = (ann.strokeWidth || 2) + 4;
      const boxW = (r * 2 + pad * 2) * scale;
      const boxH = (r * 2 + pad * 2) * scale;
      return (
        <div
          ref={dragRef}
          className={`ann ann-shape ${selected ? 'selected' : ''}`}
          style={{ left: (cx - r - pad) * scale, top: (cy - r - pad) * scale, width: boxW, height: boxH }}
          onMouseDown={(e) => onMouseDown(e, 'move')}
        >
          <svg width={boxW} height={boxH} style={{ display: 'block', pointerEvents: 'none' }}>
            <circle cx={(r + pad) * scale} cy={(r + pad) * scale} r={r * scale} fill="none" stroke={col} strokeWidth={sw} />
          </svg>
          {selected && <Handles onDelete={onDelete} {...layerProps} />}
        </div>
      );
    }
    if (kind === 'line') {
      const minX = Math.min(ann.x1, ann.x2);
      const minY = Math.min(ann.y1, ann.y2);
      const maxX = Math.max(ann.x1, ann.x2);
      const maxY = Math.max(ann.y1, ann.y2);
      const pad = (ann.strokeWidth || 2) + 4;
      const w = (maxX - minX + pad * 2) * scale;
      const h = (maxY - minY + pad * 2) * scale;
      return (
        <div
          ref={dragRef}
          className={`ann ann-shape ${selected ? 'selected' : ''}`}
          style={{ left: (minX - pad) * scale, top: (minY - pad) * scale, width: w, height: h }}
          onMouseDown={(e) => onMouseDown(e, 'move')}
        >
          <svg width={w} height={h} style={{ display: 'block', pointerEvents: 'none' }}>
            <line
              x1={(ann.x1 - minX + pad) * scale}
              y1={(ann.y1 - minY + pad) * scale}
              x2={(ann.x2 - minX + pad) * scale}
              y2={(ann.y2 - minY + pad) * scale}
              stroke={col}
              strokeWidth={sw}
              strokeLinecap="round"
            />
          </svg>
          {selected && <Handles onDelete={onDelete} {...layerProps} />}
        </div>
      );
    }
    if (kind === 'ellipse') {
      return (
        <div
          ref={dragRef}
          className={`ann ann-shape ${selected ? 'selected' : ''}`}
          style={{ left: ann.x * scale, top: ann.y * scale, width: ann.w * scale, height: ann.h * scale }}
          onMouseDown={(e) => onMouseDown(e, 'move')}
        >
          <svg width={ann.w * scale} height={ann.h * scale} style={{ display: 'block', pointerEvents: 'none' }}>
            <ellipse cx={(ann.w / 2) * scale} cy={(ann.h / 2) * scale} rx={(ann.w / 2) * scale - sw / 2} ry={(ann.h / 2) * scale - sw / 2} fill="none" stroke={col} strokeWidth={sw} />
          </svg>
          {selected && <Handles onResize={(e) => onMouseDown(e, 'resize')} onDelete={onDelete} {...layerProps} />}
        </div>
      );
    }
    if (kind === 'fillrect') {
      // Filled rectangle ("white zone") — a solid block used to cover
      // content. Background colour is the chosen draw colour.
      return (
        <div
          ref={dragRef}
          className={`ann ann-shape ann-fillrect ${selected ? 'selected' : ''}`}
          style={{
            left: ann.x * scale,
            top: ann.y * scale,
            width: ann.w * scale,
            height: ann.h * scale,
            background: col,
          }}
          onMouseDown={(e) => onMouseDown(e, 'move')}
        >
          {selected && <Handles onResize={(e) => onMouseDown(e, 'resize')} onDelete={onDelete} {...layerProps} />}
        </div>
      );
    }
    // rect (default)
    return (
      <div
        ref={dragRef}
        className={`ann ann-shape ${selected ? 'selected' : ''}`}
        style={{
          left: ann.x * scale,
          top: ann.y * scale,
          width: ann.w * scale,
          height: ann.h * scale,
          border: `${sw}px solid ${col}`,
          borderRadius: 0,
        }}
        onMouseDown={(e) => onMouseDown(e, 'move')}
      >
        {selected && <Handles onResize={(e) => onMouseDown(e, 'resize')} onDelete={onDelete} {...layerProps} />}
      </div>
    );
  }

  if (ann.type === 'draw') {
    const bbox = ann.bbox || { x: 0, y: 0, w: 0, h: 0 };
    const pad = (ann.strokeWidth || 2) * 1.2;
    return (
      <div
        ref={dragRef}
        className={`ann ann-draw ${selected ? 'selected' : ''}`}
        style={{
          left: (bbox.x - pad) * scale,
          top: (bbox.y - pad) * scale,
          width: (bbox.w + pad * 2) * scale,
          height: (bbox.h + pad * 2) * scale,
        }}
        onMouseDown={(e) => onMouseDown(e, 'move')}
      >
        <svg
          width={(bbox.w + pad * 2) * scale}
          height={(bbox.h + pad * 2) * scale}
          style={{ display: 'block', pointerEvents: 'none' }}
        >
          <polyline
            points={ann.points
              .map((p) => `${(p.x - bbox.x + pad) * scale},${(p.y - bbox.y + pad) * scale}`)
              .join(' ')}
            fill="none"
            stroke={ann.color || '#ef4444'}
            strokeWidth={(ann.strokeWidth || 2) * scale}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {selected && <Handles onDelete={onDelete} {...layerProps} />}
      </div>
    );
  }

  if (ann.type === 'image') {
    return (
      <div
        ref={dragRef}
        className={`ann ann-image ${selected ? 'selected' : ''}`}
        style={{
          left: ann.x * scale,
          top: ann.y * scale,
          width: ann.w * scale,
          height: ann.h * scale,
        }}
        onMouseDown={(e) => onMouseDown(e, 'move')}
      >
        <img src={ann.dataUrl} alt="" draggable={false} />
        {selected && <Handles onResize={(e) => onMouseDown(e, 'resize')} onDelete={onDelete} {...layerProps} />}
      </div>
    );
  }

  if (ann.type === 'text') {
    return (
      <TextAnnotation
        ann={ann}
        scale={scale}
        selected={selected}
        onMouseDown={onMouseDown}
        onBeginEdit={onBeginEdit}
        onLiveUpdate={onLiveUpdate}
        onDelete={onDelete}
        layerProps={layerProps}
      />
    );
  }
  return null;
}

function TextAnnotation({ ann, scale, selected, onMouseDown, onBeginEdit, onLiveUpdate, onDelete, layerProps }) {
  const ref = useRef(null);
  // Auto-enter edit mode only for brand new empty text or for glyphs the
  // user explicitly double-clicked to edit (_edit flag). A restyled
  // existing glyph renders as a normal selectable / draggable overlay.
  const [editing, setEditing] = useState(!ann.text || !!ann._edit);
  const snappedRef = useRef(false);
  const bound = !!ann.originalItemKey;

  useEffect(() => {
    if (editing && ref.current) {
      const el = ref.current;
      const raf = requestAnimationFrame(() => {
        el.focus();
        const r = document.createRange();
        r.selectNodeContents(el);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [editing]);

  const decorations = [ann.underline ? 'underline' : null, ann.strike ? 'line-through' : null].filter(Boolean).join(' ');
  const boxStyle = {
    left: ann.x * scale,
    top: ann.y * scale,
    fontFamily: fontFamilyToCss(ann.font),
    fontSize: (ann.size || 14) * scale,
    color: ann.color,
    fontWeight: ann.bold ? 700 : 400,
    fontStyle: ann.italic ? 'italic' : 'normal',
    textDecoration: decorations || 'none',
    lineHeight: bound ? `${ann.h * scale}px` : 1.15,
  };
  if (ann.rotation) {
    boxStyle.transform = `rotate(${ann.rotation}deg)`;
    boxStyle.transformOrigin = 'center';
  }
  if (ann.background) boxStyle.background = ann.background;
  if (bound) {
    // Anchor the override exactly on the original glyph's left edge so
    // the text does not drift; a wider (e.g. bolded) variant simply
    // extends to the right from the same start point.
    boxStyle.width = ann.w * scale;
    boxStyle.height = ann.h * scale;
    boxStyle.whiteSpace = 'nowrap';
    boxStyle.overflow = 'visible';
  }

  return (
    <div
      className={`ann ann-text ${selected ? 'selected' : ''} ${editing ? 'editing' : ''} ${bound ? 'bound' : ''}`}
      style={boxStyle}
      onMouseDown={(e) => {
        if (!editing) onMouseDown(e, 'move');
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
    >
      <div
        ref={ref}
        className="ann-text-inner"
        contentEditable={editing}
        suppressContentEditableWarning
        onInput={(e) => {
          if (!snappedRef.current) {
            snappedRef.current = true;
            onBeginEdit?.();
          }
          onLiveUpdate({ text: e.currentTarget.innerText });
        }}
        onBlur={() => {
          setEditing(false);
          snappedRef.current = false;
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.currentTarget.blur();
          }
        }}
      >
        {ann.text}
      </div>
      {selected && !editing && (
        <Handles onRotate={(e) => onMouseDown(e, 'rotate')} onDelete={onDelete} {...layerProps} />
      )}
    </div>
  );
}

function Handles({ onResize, onRotate, onDelete, onFront, onBack }) {
  return (
    <>
      {onResize && (
        <div className="ann-handle handle-br" onMouseDown={onResize} title="" />
      )}
      {onRotate && (
        <div
          className="ann-handle handle-rot"
          onMouseDown={onRotate}
          title="Rotation"
        >
          <Icon name="rotateCW" size={11} />
        </div>
      )}
      {onFront && (
        <button
          className="ann-handle handle-front"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onFront}
          title="Mettre au premier plan"
          aria-label="Bring to front"
        >
          <Icon name="front" size={11} />
        </button>
      )}
      {onBack && (
        <button
          className="ann-handle handle-back"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onBack}
          title="Mettre à l'arrière-plan"
          aria-label="Send to back"
        >
          <Icon name="back" size={11} />
        </button>
      )}
      <button
        className="ann-handle handle-del"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={onDelete}
        aria-label="Delete annotation"
      >
        <Icon name="trash" size={12} />
      </button>
    </>
  );
}

export const AnnotationLayer = memo(AnnotationLayerBase);
